import React, { useState, useEffect } from 'react';
import {
  BarChart2,
  Download,
  AlertTriangle,
  Users,
  HeartPulse,
  FileCheck,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  FileText,
  Sheet,
} from 'lucide-react';
import { format, subMonths } from 'date-fns';
import toast from 'react-hot-toast';

import { Link } from 'react-router-dom';
import api from '../api/axios';
import { PersonName } from '../components/common/PersonColor';
import LoadingSpinner from '../components/common/LoadingSpinner';
import WhistleIcon from '../components/common/WhistleIcon';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtHrs(mins) {
  if (!mins) return '0h 0m';
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function fmtTime(dtStr) {
  if (!dtStr) return '—';
  return dtStr.slice(11, 16);
}

function entryTypeLabel(type, au) {
  if (type === 'sick_leave') return au ? 'Sick Leave (AU)' : 'Sick (no AU)';
  if (type === 'vacation') return 'Vacation';
  return 'Regular';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function UserRow({ u, expanded, onToggle }) {
  const hasActivity =
    u.regular_minutes > 0 ||
    u.sick_minutes > 0 ||
    u.vacation_weeks > 0 ||
    u.bonus_minutes > 0 ||
    u.sick_days_with_au > 0 ||
    u.sick_days_without_au > 0;

  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: 'pointer', background: expanded ? 'var(--primary-xlight)' : undefined }}
      >
        <td style={{ fontWeight: 600 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <PersonName userId={u.id} name={u.name} />
          </span>
        </td>
        <td>{fmtHrs(u.regular_minutes)}</td>
        <td>{fmtHrs(u.sick_minutes)}</td>
        <td style={{ textAlign: 'center' }}>{u.sick_days_with_au}</td>
        <td style={{ textAlign: 'center' }}>{u.sick_days_without_au}</td>
        <td>
          {u.vacation_weeks > 0 ? (
            <span title={u.avg_weekly_minutes != null ? `Avg ${fmtHrs(u.avg_weekly_minutes)}/week` : undefined}>
              {u.vacation_weeks}w · {fmtHrs(u.vacation_minutes)}
            </span>
          ) : (
            '—'
          )}
        </td>
        <td>{fmtHrs(u.bonus_minutes)}</td>
        <td style={{ textAlign: 'center' }}>
          {u.unpaid_leave_days > 0 ? `${u.unpaid_leave_days}d` : '—'}
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} style={{ padding: 0, background: 'var(--bg)' }}>
            {u.shifts.length === 0 ? (
              <p style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                No shifts logged.
              </p>
            ) : (
              <table style={{ width: '100%', fontSize: '0.83rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--border)' }}>
                    <th style={{ padding: '6px 16px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Shift</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Clock In</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Clock Out</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Hours</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {u.shifts.map((s) => (
                    <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '5px 16px' }}>{s.date}</td>
                      <td style={{ padding: '5px 8px' }}>{s.template_name}</td>
                      <td style={{ padding: '5px 8px' }}>{fmtTime(s.clocked_in_at)}</td>
                      <td style={{ padding: '5px 8px' }}>{fmtTime(s.clocked_out_at)}</td>
                      <td style={{ padding: '5px 8px' }}>{fmtHrs(s.total_minutes)}</td>
                      <td style={{ padding: '5px 8px' }}>
                        <span
                          className={`badge ${
                            s.entry_type === 'sick_leave'
                              ? 'badge-danger'
                              : s.entry_type === 'vacation'
                              ? 'badge-info'
                              : 'badge-success'
                          }`}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {entryTypeLabel(s.entry_type, s.au_certificate_submitted)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

function ReportsPage() {
  const [selectedMonth, setSelectedMonth] = useState(() =>
    format(subMonths(new Date(), 1), 'yyyy-MM')
  );
  const [reportData, setReportData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);

  // Payroll summary + timesheet state
  const [payrollXlsxBusy, setPayrollXlsxBusy] = useState(false);
  const [payrollCsvBusy,  setPayrollCsvBusy]  = useState(false);
  const [tsPdfBusy,       setTsPdfBusy]       = useState(false);
  const [tsUserId,        setTsUserId]        = useState('');
  const [tsUsers,         setTsUsers]         = useState([]);

  // Trainer bonus per hour on training shifts (€), one admin setting
  const [trainerRate,      setTrainerRate]      = useState('');
  const [trainerRateSaved, setTrainerRateSaved] = useState('');
  const [trainerRateBusy,  setTrainerRateBusy]  = useState(false);

  useEffect(() => {
    api.get('/users').then(res => setTsUsers(res.data)).catch(() => {});
    api.get('/reports/trainer-rate').then(res => {
      const v = Number(res.data.rate).toFixed(2);
      setTrainerRate(v);
      setTrainerRateSaved(v);
    }).catch(() => {});
  }, []);

  const saveTrainerRate = async () => {
    setTrainerRateBusy(true);
    try {
      const res = await api.put('/reports/trainer-rate', { rate: trainerRate });
      const v = Number(res.data.rate).toFixed(2);
      setTrainerRate(v);
      setTrainerRateSaved(v);
      toast.success('Trainer bonus saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the trainer bonus.');
    } finally {
      setTrainerRateBusy(false);
    }
  };

  async function blobDownload(url, filename) {
    const res = await api.get(url, { responseType: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(res.data);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  const handlePayrollDownload = async (fmt) => {
    const busy = fmt === 'xlsx' ? setPayrollXlsxBusy : setPayrollCsvBusy;
    busy(true);
    try {
      const ext = fmt === 'xlsx' ? 'xlsx' : 'csv';
      await blobDownload(
        `/reports/payroll-summary?month=${selectedMonth}&format=${fmt}`,
        `lohnabrechnung-${selectedMonth}.${ext}`
      );
    } catch {
      toast.error('Failed to download payroll summary.');
    } finally {
      busy(false);
    }
  };

  const handleTimesheetDownload = async () => {
    setTsPdfBusy(true);
    try {
      const userParam = tsUserId ? `&user_id=${tsUserId}` : '';
      const namePart  = tsUserId
        ? (tsUsers.find(u => String(u.id) === tsUserId)?.name || 'user').replace(/\s+/g, '-').toLowerCase()
        : 'alle';
      await blobDownload(
        `/reports/timesheet?month=${selectedMonth}${userParam}`,
        `stundennachweis-${selectedMonth}-${namePart}.pdf`
      );
    } catch {
      toast.error('Failed to download time sheet.');
    } finally {
      setTsPdfBusy(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setReportData(null);
    setExpandedUser(null);
    try {
      const res = await api.get(`/reports/monthly?month=${selectedMonth}`);
      setReportData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate report.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/monthly/export?month=${selectedMonth}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leuchtstoff-report-${selectedMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download spreadsheet.');
    } finally {
      setDownloading(false);
    }
  };

  const toggleUser = (id) => setExpandedUser((prev) => (prev === id ? null : id));

  // Summary totals across all users
  const totals = reportData
    ? reportData.users.reduce(
        (acc, u) => {
          acc.regular += u.regular_minutes;
          acc.sick += u.sick_minutes;
          acc.bonus += u.bonus_minutes;
          return acc;
        },
        { regular: 0, sick: 0, bonus: 0 }
      )
    : null;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BarChart2 size={26} style={{ color: 'var(--primary-dark)' }} />
            Reports
          </h1>
          <p className="page-subtitle">Monthly time tracking &amp; payout summary</p>
        </div>
      </div>

      {/* Controls */}
      <div
        className="card"
        style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}
      >
        <div className="form-group" style={{ margin: 0, flex: '0 0 auto' }}>
          <label className="form-label" style={{ marginBottom: '4px' }}>
            Report Month
          </label>
          <input
            type="month"
            className="form-input"
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(e.target.value);
              setReportData(null);
            }}
            style={{ width: '180px' }}
          />
        </div>
        <button
          className="btn btn-primary"
          onClick={handleGenerate}
          disabled={generating}
          style={{ marginTop: '20px' }}
        >
          <RefreshCw size={15} />
          {generating ? 'Generating…' : 'Generate Report'}
        </button>
      </div>

      {/* Payroll Summary */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <p style={{ fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sheet size={16} style={{ color: '#3B82F6' }} />
              Payroll Summary
            </p>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              One row per employee — for the payroll office. No Generate needed.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePayrollDownload('xlsx')}
              disabled={payrollXlsxBusy}
            >
              <Download size={14} />
              {payrollXlsxBusy ? 'Downloading…' : 'Download XLSX'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePayrollDownload('csv')}
              disabled={payrollCsvBusy}
            >
              <Download size={14} />
              {payrollCsvBusy ? 'Downloading…' : 'Download CSV'}
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 10px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
          <label htmlFor="trainer-rate" style={{ fontSize: '0.88rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <WhistleIcon size={15} title="Training shift" />
            Trainer bonus per hour (€)
          </label>
          <input
            id="trainer-rate"
            className="form-input"
            inputMode="decimal"
            value={trainerRate}
            onChange={(e) => setTrainerRate(e.target.value)}
            style={{ width: '90px', minHeight: '40px' }}
            disabled={trainerRateBusy}
          />
          <button
            className="btn btn-secondary btn-sm"
            style={{ minHeight: '40px' }}
            onClick={saveTrainerRate}
            disabled={trainerRateBusy || trainerRate === trainerRateSaved}
          >
            {trainerRateBusy ? 'Saving…' : 'Save'}
          </button>
          <p style={{ flexBasis: '100%', margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Columns "Trainerstunden" and "Trainerbonus": hours tracked on training shifts × this rate. Sick shifts don&apos;t count.
          </p>
        </div>
      </div>

      {/* Time Sheets */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <p style={{ fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={16} style={{ color: '#3B82F6' }} />
              Time Sheets
            </p>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              Printable A4 time sheet per employee. No Generate needed.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              className="form-input"
              style={{ width: 'auto', minWidth: '180px' }}
              value={tsUserId}
              onChange={e => setTsUserId(e.target.value)}
            >
              <option value="">All employees</option>
              {tsUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <button
              className="btn btn-secondary"
              onClick={handleTimesheetDownload}
              disabled={tsPdfBusy}
            >
              <Download size={14} />
              {tsPdfBusy ? 'Downloading…' : 'Download PDF'}
            </button>
          </div>
        </div>
      </div>

      {generating && <LoadingSpinner />}

      {reportData && (
        <>
          {/* Uncovered Sundays warning */}
          {reportData.uncovered_sundays.length > 0 && (
            <div
              style={{
                background: 'var(--warning-light)',
                border: '1px solid var(--warning)',
                borderRadius: 'var(--radius)',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '20px',
              }}
            >
              <AlertTriangle size={18} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: '1px' }} />
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  {reportData.uncovered_sundays.length} Sunday
                  {reportData.uncovered_sundays.length > 1 ? 's' : ''} without a bonus declaration
                </p>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: '2px' }}>
                  {reportData.uncovered_sundays.join(', ')} — go to the Roster to declare bonuses before downloading.
                </p>
              </div>
            </div>
          )}

          {/* Overview stat cards */}
          {reportData.users.length > 0 && (
            <div className="grid-3" style={{ gap: '16px', marginBottom: '24px' }}>
              <div className="stat-card">
                <div className="stat-icon stat-icon-primary">
                  <Users size={22} />
                </div>
                <div className="stat-content">
                  <p className="stat-label">Active employees</p>
                  <p className="stat-value">{reportData.users.length}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon stat-icon-success">
                  <BarChart2 size={22} />
                </div>
                <div className="stat-content">
                  <p className="stat-label">Total regular hours</p>
                  <p className="stat-value" style={{ fontSize: '1.2rem' }}>
                    {fmtHrs(totals.regular)}
                  </p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon stat-icon-danger">
                  <HeartPulse size={22} />
                </div>
                <div className="stat-content">
                  <p className="stat-label">Total sick hours</p>
                  <p className="stat-value" style={{ fontSize: '1.2rem' }}>
                    {fmtHrs(totals.sick)}
                  </p>
                  {totals.bonus > 0 && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '2px' }}>
                      +{fmtHrs(totals.bonus)} bonus
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Per-user breakdown table */}
          <div className="card" style={{ marginBottom: '24px', padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <p className="card-title">Employee Breakdown</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                Click a row to see individual shift details.
              </p>
            </div>

            {reportData.users.length === 0 ? (
              <div
                className="empty-state"
                style={{ padding: '48px 24px' }}
              >
                <div className="empty-state-icon">
                  <BarChart2 size={32} />
                </div>
                <p>No time entries recorded for this month.</p>
              </div>
            ) : (
              <div className="table-wrapper" style={{ margin: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Regular Hrs</th>
                      <th>Sick Hrs</th>
                      <th style={{ textAlign: 'center' }}>Days w/ AU</th>
                      <th style={{ textAlign: 'center' }}>Days w/o AU</th>
                      <th>Vacation</th>
                      <th>Bonus Hrs</th>
                      <th style={{ textAlign: 'center' }}>Unpaid Leave</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.users.map((u) => (
                      <UserRow
                        key={u.id}
                        u={u}
                        expanded={expandedUser === u.id}
                        onToggle={() => toggleUser(u.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Team Highlights */}
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '12px', color: 'var(--text)' }}>
              Team Highlights
            </h2>
            <div className="grid-3" style={{ gap: '16px' }}>
              {/* Top covers */}
              <div className="card">
                <p className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                  <Users size={15} style={{ color: 'var(--primary-dark)' }} />
                  Most Sick Shift Covers
                </p>
                {reportData.highlights.top_covers.length === 0 ? (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>None this month.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {reportData.highlights.top_covers.map((c) => (
                      <div
                        key={c.user_id}
                        style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}
                      >
                        <PersonName userId={c.user_id} name={c.user_name} />
                        <span className="badge badge-success">{c.cover_count} cover{c.cover_count > 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sickness overview */}
              <div className="card">
                <p className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                  <HeartPulse size={15} style={{ color: 'var(--danger)' }} />
                  Sickness Overview
                </p>
                {reportData.highlights.sick_stats.length === 0 ? (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No sickness records.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {reportData.highlights.sick_stats.map((s) => (
                      <div key={s.user_id} style={{ fontSize: '0.88rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <PersonName userId={s.user_id} name={s.user_name} />
                          <span className="badge badge-danger">{s.sick_count} day{s.sick_count > 1 ? 's' : ''}</span>
                        </div>
                        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {s.sick_count - s.without_au} w/ AU · {s.without_au} w/o AU
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Missing AU */}
              <div className="card">
                <p className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                  <FileCheck size={15} style={{ color: 'var(--warning)' }} />
                  Missing AU Certificates
                </p>
                {reportData.highlights.missing_au.length === 0 ? (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-success, var(--success))' }}>
                    All AU certificates submitted.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {reportData.highlights.missing_au.map((m) => (
                      <div
                        key={m.user_id}
                        style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}
                      >
                        <PersonName userId={m.user_id} name={m.user_name} />
                        <span className="badge badge-warning">{m.without_au} missing</span>
                      </div>
                    ))}
                  </div>
                )}
                <Link
                  to={`/sick-notes?month=${reportData.month}&missing=1`}
                  style={{ display: 'inline-block', marginTop: '10px', fontSize: '0.84rem', fontWeight: 600, color: 'var(--primary-dark)' }}
                >
                  Open sick notes overview →
                </Link>
              </div>
            </div>
          </div>

          {/* Download button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={handleDownload}
              disabled={downloading}
            >
              <Download size={15} />
              {downloading ? 'Downloading…' : 'Download Spreadsheet (.xlsx)'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ReportsPage;
